"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { GroupDescriptionType } from "@/entities/IGroup";
import type { ApiGroupDescription } from "@/generated/models/ApiGroupDescription";
import { ApiGroupFilterDirection } from "@/generated/models/ApiGroupFilterDirection";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { ApiGroupTdhInclusionStrategy } from "@/generated/models/ApiGroupTdhInclusionStrategy";
import { ApiXTdhGrantStatus } from "@/generated/models/ApiXTdhGrantStatus";
import { toShortGrantId } from "@/components/groups/page/create/config/xtdh-grant/utils";
import GroupCardConfig from "./GroupCardConfig";
export interface GroupCardConfigProps {
  readonly key: GroupDescriptionType;
  readonly value: string;
  readonly label?: string | undefined;
  readonly tooltip?: string | undefined;
  readonly muted?: boolean | undefined;
}
const MANUAL_LIST_TOOLTIP = "Wallets explicitly listed in this group. Filter-matching wallets are not counted here.";
const GRANT_TOOLTIP = "Identity must be a beneficiary of the selected xTDH grant.";
const GRANT_STATUS_LABELS: Record<ApiXTdhGrantStatus, string> = {
  [ApiXTdhGrantStatus.Pending]: "PENDING",
  [ApiXTdhGrantStatus.Failed]: "FAILED",
  [ApiXTdhGrantStatus.Disabled]: "REVOKED",
  [ApiXTdhGrantStatus.Granted]: "GRANTED"
};
export default function GroupCardConfigs(t0) {
  const $ = _c(25);
  const {
    group
  } = t0;
  const [nowMs] = useState(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiGroupFilterDirection.Received]: "from",
      [ApiGroupFilterDirection.Sent]: "to"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const directionLabels = t1;
  const getMinMaxValue = _temp2;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = t3 => {
      const {
        identity,
        direction
      } = t3;
      if (!identity) {
        return null;
      }
      const directionPrefix = direction ? `${directionLabels[direction]} ` : "";
      return `${directionPrefix}identity: ${identity}`;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getIdentityValue = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = tdh => {
      const value = getMinMaxValue({
        min: tdh.min,
        max: tdh.max
      });
      if (!value) {
        return null;
      }
      let label = "Tdh";
      if (tdh.inclusion_strategy === ApiGroupTdhInclusionStrategy.Xtdh) {
        label = "xTDH";
      } else {
        if (tdh.inclusion_strategy === ApiGroupTdhInclusionStrategy.Both) {
          label = "TDH + xTDH";
        }
      }
      return {
        key: GroupDescriptionType.TDH,
        value,
        label
      };
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const getTdhConfig = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = rep => {
      const value_0 = getMinMaxValue({
        min: rep.min,
        max: rep.max
      });
      const category = typeof rep.category?.length === "number" && rep.category.length > 0 ? `category: ${rep.category}` : null;
      const identity_0 = getIdentityValue({
        identity: rep.user_identity,
        direction: rep.direction
      });
      const parts = [value_0, category, identity_0].filter(Boolean);
      if (!parts.length) {
        return null;
      }
      return {
        key: GroupDescriptionType.REP,
        value: parts.join(", ")
      };
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const getRepConfig = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cic => {
      const value_1 = getMinMaxValue({
        min: cic.min,
        max: cic.max
      });
      const identity_1 = getIdentityValue({
        identity: cic.user_identity,
        direction: cic.direction
      });
      const parts_0 = [value_1, identity_1].filter(Boolean);
      if (!parts_0.length) {
        return null;
      }
      return {
        key: GroupDescriptionType.NIC,
        value: parts_0.join(", ")
      };
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const getCicConfig = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = level => {
      const value_2 = getMinMaxValue({
        min: level.min,
        max: level.max
      });
      if (!value_2) {
        return null;
      }
      return {
        key: GroupDescriptionType.LEVEL,
        value: value_2
      };
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const getLevelConfig = t6;
  const getWalletsConfig = _temp3;
  let t7;
  if ($[6] !== nowMs) {
    const getGrantStatusLabel = grant => {
      if (grant?.status === undefined) {
        return null;
      }
      if (grant.status === ApiXTdhGrantStatus.Granted) {
        const from = grant.valid_from ?? null;
        const to = grant.valid_to ?? null;
        if (typeof to === "number" && to > 0 && to < nowMs) {
          return "ENDED";
        }
        if (typeof from === "number" && from > nowMs) {
          return "SCHEDULED";
        }
        return "ACTIVE";
      }
      return GRANT_STATUS_LABELS[grant.status];
    };
    t7 = groupDescription => {
      const grantId = groupDescription.is_beneficiary_of_grant_id;
      if (!grantId) {
        return null;
      }
      const statusLabel = getGrantStatusLabel(groupDescription.is_beneficiary_of_grant);
      const shortGrantId = toShortGrantId(grantId);
      const value_3 = statusLabel ? `${statusLabel} (${shortGrantId})` : shortGrantId;
      return {
        key: GroupDescriptionType.XTDH_GRANT,
        value: value_3,
        label: "Grant",
        tooltip: GRANT_TOOLTIP
      };
    };
    $[6] = nowMs;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const getGrantConfig = t7;
  let t8;
  if ($[8] !== getGrantConfig || $[9] !== group) {
    const getConfigs = () => {
      if (!group) {
        return [{
          key: GroupDescriptionType.WALLETS,
          value: "No manual list",
          label: "Manual list",
          tooltip: MANUAL_LIST_TOOLTIP,
          muted: true
        }];
      }
      const configs = [];
      const {
        tdh: tdh_0,
        rep: rep_0,
        cic: cic_0,
        level: level_0,
        identity_group_identities_count
      } = group.group;
      const tdhConfig = getTdhConfig(tdh_0);
      const repConfig = getRepConfig(rep_0);
      const cicConfig = getCicConfig(cic_0);
      const levelConfig = getLevelConfig(level_0);
      const grantConfig = getGrantConfig(group.group);
      const walletsConfig = getWalletsConfig(identity_group_identities_count);
      if (tdhConfig) {
        configs.push(tdhConfig);
      }
      if (repConfig) {
        configs.push(repConfig);
      }
      if (cicConfig) {
        configs.push(cicConfig);
      }
      if (levelConfig) {
        configs.push(levelConfig);
      }
      if (grantConfig) {
        configs.push(grantConfig);
      }
      configs.push(walletsConfig);
      return configs;
    };
    t8 = getConfigs();
    $[8] = getGrantConfig;
    $[9] = group;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const configs_0 = t8;
  const [isLeftHidden, setIsLeftHidden] = useState(false);
  const [isRightHidden, setIsRightHidden] = useState(true);
  const containerRef = useRef(null);
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const container = containerRef.current;
      if (container) {
        const {
          scrollLeft,
          scrollWidth,
          clientWidth
        } = container;
        setIsLeftHidden(scrollLeft > 0);
        setIsRightHidden(scrollLeft < scrollWidth - clientWidth);
      }
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const checkForHiddenContent = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = direction_0 => {
      const container_0 = containerRef.current;
      if (container_0) {
        const scrollAmount = direction_0 === "left" ? -200 : 200;
        container_0.scrollBy({
          left: scrollAmount,
          behavior: "smooth"
        });
      }
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const scrollContainer = t10;
  let t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      checkForHiddenContent();
      const container_1 = containerRef.current;
      const canObserve = typeof ResizeObserver !== "undefined" && !!containerRef.current;
      const observer = canObserve ? new ResizeObserver(() => checkForHiddenContent()) : null;
      if (observer && container_1) {
        observer.observe(container_1);
      } else {
        window.addEventListener("resize", checkForHiddenContent);
      }
      return () => {
        if (observer && container_1) {
          observer.unobserve(container_1);
          observer.disconnect();
        }
        window.removeEventListener("resize", checkForHiddenContent);
      };
    };
    t12 = [];
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  useEffect(t11, t12);
  let t13;
  if ($[15] !== isLeftHidden) {
    t13 = isLeftHidden && <button onClick={e => {
      e.stopPropagation();
      scrollContainer("left");
    }} aria-label="Scroll left" className="tw-absolute tw-left-0 tw-top-1/2 tw-z-30 tw-inline-flex tw-h-7 tw-w-7 -tw-translate-x-3 tw-translate-y-[-50%] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-iron-800 tw-text-white tw-transition tw-duration-200 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-500 desktop-hover:hover:tw-bg-white/10"><svg className="tw-h-4 tw-w-4 tw-rotate-90 tw-text-iron-200 tw-transition tw-duration-200 tw-ease-out desktop-hover:hover:tw-text-white" viewBox="0 0 24 24" aria-hidden="true" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>;
    $[15] = isLeftHidden;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== configs_0) {
    t14 = <div className="horizontal-menu-hide-scrollbar tw-flex tw-items-center tw-gap-x-4 tw-gap-y-2 tw-overflow-x-auto tw-py-0.5" ref={containerRef} onScroll={checkForHiddenContent}>{configs_0.map(_temp4)}</div>;
    $[17] = configs_0;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== isRightHidden) {
    t15 = isRightHidden && <button onClick={e_0 => {
      e_0.stopPropagation();
      scrollContainer("right");
    }} aria-label="Scroll right" className="tw-absolute tw-right-0 tw-top-1/2 tw-z-30 tw-inline-flex tw-h-7 tw-w-7 tw-translate-x-3 tw-translate-y-[-50%] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-iron-800 tw-text-white tw-transition tw-duration-200 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-500 desktop-hover:hover:tw-bg-white/10"><svg className="tw-h-4 tw-w-4 -tw-rotate-90 tw-text-iron-200 tw-transition tw-duration-200 tw-ease-out desktop-hover:hover:tw-text-white" viewBox="0 0 24 24" aria-hidden="true" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg></button>;
    $[19] = isRightHidden;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t13 || $[22] !== t14 || $[23] !== t15) {
    t16 = <div className="tw-relative tw-flex tw-items-start tw-text-xs tw-text-iron-200 sm:tw-text-sm"><div className="tw-w-full tw-overflow-x-hidden">{t13}{t14}{t15}</div></div>;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  return t16;
}
function _temp4(config) {
  return <GroupCardConfig config={config} key={config.key} />;
}
function _temp3(wallet_group_wallets_count) {
  const hasManualList = wallet_group_wallets_count > 0;
  return {
    key: GroupDescriptionType.WALLETS,
    value: hasManualList ? `${wallet_group_wallets_count}` : "No manual list",
    label: "Manual list",
    tooltip: MANUAL_LIST_TOOLTIP,
    muted: !hasManualList
  };
}
function _temp2(t0) {
  const {
    min,
    max
  } = t0;
  if (min === null && max === null) {
    return null;
  }
  if (min === null) {
    return `<= ${max}`;
  }
  if (max === null) {
    return `>= ${min}`;
  }
  return `${min} - ${max}`;
}
function _temp() {
  return Date.now();
}